import './file-uploader.scss';

import {
  Box,
  Grid,
  IconButton,
  Paper,
  Stack,
  styled,
  Typography,
} from '@mui/material';
import { DropzoneOptions, FileRejection, useDropzone } from 'react-dropzone';
import React, { useCallback, useState } from 'react';
import config, { convergeLogger } from '@converge/config';

import { ApolloError } from '@apollo/client';
import BackupOutlinedIcon from '@mui/icons-material/BackupOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { ConvergeInputLabel } from './converge-input-label';
import { NewConvergeButton } from './new-button';
import { UploadFolderFormSearchableSelectField } from './form-searchable-select-field';
import { UploadFolderName, Attachment } from '@converge/graphql';
import { useConvergeSnackBar } from '../../contexts';
import { v4 as uuidv4 } from 'uuid';

const UploadWrapper = styled(Box)((props) => ({
  [props.theme.breakpoints.up('md')]: {
    display: 'flex',
    flexDirection: 'column',
  },
}));
const UploadProgressWrapper = styled(Box)((props) => ({
  height: '294px',
  overflowY: 'auto',
  paddingRight: '15px',
  [props.theme.breakpoints.up('md')]: {
    flexBasis: '55%',
  },
}));

export interface UploadFile {
  id: string;
  file: File;
  destination?: string;
  error?: Error | ApolloError;
  folder: UploadFolderName;
}

interface INewFileUploader extends DropzoneOptions {
  onFileHandler: (data: UploadOnDropProps) => void;
  onFileRemove?: (acceptedFiles: UploadFile[]) => void;
  label: string;
  hiddenLabel?: boolean;
  required?: boolean;
  progressBar?: boolean;
  showRemoveIcon?: boolean;
  enableFolderSelection?: boolean;
  hideFolderSelection?: boolean; // New prop to hide folder selection
  folder?: UploadFolderName;
  verb?: 'upload' | 'attach';
  existingAttachments?: Attachment[]; // Existing attachments to display
  onExistingAttachmentRemove?: (attachmentUuid: string) => void; // Callback for removing existing attachments
}

export interface UploadProgress {
  [key: string]: number;
}
export interface UploadOnDropProps {
  acceptedFiles: UploadFile[];
  fileRejections: FileRejection[];
  allAcceptedFiles: UploadFile[];
  onProgressChange: (event: ProgressEvent, file: UploadFile) => void;
  onError: (error: Error | ApolloError, file: UploadFile) => void;
}

export const NewFileUploader: React.FC<INewFileUploader> = ({
  onFileHandler,
  onFileRemove,
  label,
  hiddenLabel = false,
  required = false,
  progressBar = true,
  showRemoveIcon = false,
  verb = 'upload',
  maxSize = config.maxFileSizeInBytes,
  enableFolderSelection = false,
  hideFolderSelection = false, // New prop with default false
  folder = UploadFolderName.ApplicationForms,
  existingAttachments = [],
  onExistingAttachmentRemove,
  ...otherProps
}) => {
  const [targetFolder, setTargetFolder] = useState<UploadFolderName | null>(
    folder ?? null
  );
  const [uploadProgress, setUploadProgress] = useState<UploadProgress>({});
  const [files, setFiles] = useState<UploadFile[]>([]);
  const { notify } = useConvergeSnackBar();

  const onProgressChange = useCallback(
    (event: ProgressEvent, file: UploadFile) => {
      setUploadProgress((prevProgress) => ({
        ...prevProgress,
        [file.id]: Math.round((event.loaded * 100) / event.total),
      }));
    },
    []
  );

  const onDropHandle = useCallback(
    (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      const newAcceptedFiles: UploadFile[] = acceptedFiles.map(
        (file): UploadFile => {
          return {
            id: uuidv4(),
            file,
            folder: targetFolder ?? folder ?? undefined,
          };
        }
      );
      const allFiles = [...files, ...newAcceptedFiles];
      convergeLogger.log(`All files:`, allFiles);
      setFiles(() => [...allFiles]);
      onFileHandler({
        acceptedFiles: newAcceptedFiles,
        fileRejections,
        allAcceptedFiles: allFiles,
        onProgressChange,
        onError: (error: Error | ApolloError, file: UploadFile) => {
          setFiles((prevFiles) => {
            const fileToUpdate = prevFiles.find((f) => f.id === file.id);
            if (fileToUpdate) {
              fileToUpdate.error = error;
            }
            return prevFiles;
          });
        },
      });
    },
    [files, folder, onFileHandler, onProgressChange, targetFolder]
  );

  const onRemove = useCallback(
    (id: string) => {
      const previousUploadProgress = uploadProgress;
      delete previousUploadProgress[id];
      setUploadProgress({ ...previousUploadProgress });
      const allFiles = files.filter((file) => file.id !== id);
      setFiles(allFiles);
      if (onFileRemove) {
        onFileRemove(allFiles);
      }
    },
    [files, onFileRemove, uploadProgress]
  );

  const {
    getRootProps,
    getInputProps,
    open,
    isDragAccept,
    isFocused,
    isDragReject,
  } = useDropzone({
    onDrop: onDropHandle,
    noClick: true,
    noKeyboard: true,
    maxSize,
    onDropRejected: (rejectedFiles) => {
      const errorMessage = (
        rejectedFiles[0]?.errors[0]?.message ?? 'Unknown error'
      ).replace(`${maxSize} bytes`, `${maxSize / 1000000} MB`);

      notify({
        message: `Unable to ${verb} file: ${errorMessage}`,
        variant: 'error',
      });
    },
    ...otherProps,
  });

  return (
    <Grid item xs={12}>
      {hiddenLabel !== true && (
        <ConvergeInputLabel label={label} required={required} />
      )}
      <UploadWrapper className="file-upload">
        <Paper
          variant="outlined"
          className="file-upload-area"
          {...getRootProps({ isDragAccept, isFocused, isDragReject })}
          onDragOver={(e) => {
            e.preventDefault();
          }}
          onDrop={(e) => {
            e.preventDefault();
          }}
        >
          <input {...getInputProps()} data-testid="file-input" />

          <BackupOutlinedIcon className="upload-icon" />
          <Typography className="text-spacing drag-drop-text">
            DRAG & DROP HERE
          </Typography>
          <Typography className="text-spacing drag-drop-text-2">OR</Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <NewConvergeButton
              label={'SELECT FILE'}
              variant="outlined"
              className="file-select-button"
              onClick={open}
            />
          </Box>

          {enableFolderSelection && !hideFolderSelection && (
            <>
              <Typography className="specify-folder-label">
                <small>Specify a folder:</small>
              </Typography>
              <Stack className="specify-folder-dropdown">
                <UploadFolderFormSearchableSelectField
                  minDropdownWidth="330px"
                  maxDropdownWidth="380px"
                  name={'folder'}
                  placeholder="Select Folder"
                  hideLabel
                  value={targetFolder ?? undefined}
                  onChangeOption={(option) => {
                    setTargetFolder(option?.data?.name ?? null);
                  }}
                  containerWidth={12}
                />
              </Stack>
            </>
          )}
        </Paper>
        {/* Display existing attachments */}
        {existingAttachments && existingAttachments.length > 0 && (
          <UploadProgressWrapper className="file-upload-progress-bar-wrapper">
            {existingAttachments.map((attachment) => (
              <Box
                key={attachment.uuid}
                className="file-upload-wrapper"
                sx={{
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  mb: 1,
                }}
              >
                <span
                  className="progress-bar-details"
                  style={{ width: '100%' }}
                >
                  <span
                    className="progress-bar-details-inner"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      width: '100%',
                    }}
                  >
                    {onExistingAttachmentRemove && (
                      <span
                        className="progress-bar-remove-icon"
                        style={{ marginRight: '8px' }}
                      >
                        <IconButton
                          aria-label="remove-existing-attachment"
                          className="remove-icon"
                          onClick={() =>
                            onExistingAttachmentRemove(attachment.uuid)
                          }
                          size="small"
                          sx={{ minWidth: 'auto', paddingTop: '1rem' }}
                        >
                          <DeleteOutlineOutlinedIcon fontSize="small" />
                        </IconButton>
                      </span>
                    )}
                    <Typography
                      variant="body2"
                      component="a"
                      href={attachment.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        textDecoration: 'none',
                        color: '#1976d2',
                        flex: 1,
                        '&:hover': {
                          textDecoration: 'underline',
                        },
                      }}
                      className="progress-bar-file-name"
                    >
                      {attachment.targetName ||
                        attachment.filePath ||
                        'Download File'}
                    </Typography>
                  </span>
                </span>
              </Box>
            ))}
          </UploadProgressWrapper>
        )}
        {/* Display selected files */}
        {files && files.length > 0 && (
          <UploadProgressWrapper className="file-upload-progress-bar-wrapper">
            {files.map((file) => (
              <Box
                key={file.id}
                className="file-upload-wrapper"
                sx={{
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  mb: 1,
                }}
              >
                <span
                  className="progress-bar-details"
                  style={{ width: '100%' }}
                >
                  <span
                    className="progress-bar-details-inner"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      width: '100%',
                    }}
                  >
                    {showRemoveIcon && (
                      <span
                        className="progress-bar-remove-icon"
                        style={{ marginRight: '8px' }}
                      >
                        <IconButton
                          aria-label="remove-file"
                          className="remove-icon"
                          onClick={() => onRemove(file.id)}
                          size="small"
                          sx={{ minWidth: 'auto', paddingTop: '1rem' }}
                        >
                          <DeleteOutlineOutlinedIcon fontSize="small" />
                        </IconButton>
                      </span>
                    )}
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#333',
                        flex: 1,
                      }}
                      className="progress-bar-file-name"
                    >
                      {file.file.name}
                    </Typography>
                  </span>
                </span>
              </Box>
            ))}
          </UploadProgressWrapper>
        )}
        {/* {files && files.length && progressBar ? (
          <UploadProgressWrapper className="file-upload-progress-bar-wrapper">
            {files.map((file, index) => (
              <Box
                key={index}
                className={`file-upload-progress-bar ${
                  progressBar ? 'progress-bar' : ''
                }`}
                justifyContent={'center'}
                alignItems={'center'}
              >
                <span className="progress-bar-icon">
                  {file.error ? (
                    <ConvergeTooltip
                      title={file.error.message ?? 'Unknown error'}
                    >
                      <WarningIcon className="warning-icon" color={'error'} />
                    </ConvergeTooltip>
                  ) : null}
                </span>
                {progressBar ? (
                  <span className="progress-wrapper">
                    <LinearProgress
                      sx={{
                        width: '100%',
                        display: progressBar ? 'block' : 'none',
                      }}
                      color={file.error ? 'error' : 'primary'}
                      variant="determinate"
                      value={uploadProgress[file.id] || 0}
                      className="progress"
                    />
                  </span>
                ) : (
                  false
                )}

                <span className="progress-bar-details">
                  {progressBar && (
                    <span className="progress-bar-details-inner">
                      <span className="progress-bar-file-name">
                        {file.file.name}
                      </span>
                      <span className="progress-bar-file-size">
                        {bytesToSize(file.file.size)}
                      </span>
                    </span>
                  )}
                  {showRemoveIcon ? (
                    <span className="progress-bar-remove-icon">
                      <IconButton
                        aria-label="remove-icon"
                        disabled={
                          uploadProgress[file.id] !== 100 && progressBar
                        }
                        className="remove-icon"
                        onClick={() => onRemove(file.id)}
                      >
                        <CloseIcon className="close-icon" />
                      </IconButton>
                    </span>
                  ) : (
                    false
                  )}
                </span>
                {progressBar && (
                  <span className="progress-bar-file-percentage">
                    <ConvergeTooltip
                      title={
                        file.error?.message ??
                        `${uploadProgress[file.id] || 0}% complete`
                      }
                    >
                      <span>
                        {file.error
                          ? 'Failed to upload'
                          : `${uploadProgress[file.id] || 0}%`}
                      </span>
                    </ConvergeTooltip>
                  </span>
                )}
              </Box>
            ))}
          </UploadProgressWrapper>
        ) : (
          false
        )} */}
      </UploadWrapper>
    </Grid>
  );
};
